import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { UserX, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface BlockedUser {
  id: string;
  blocked_id: string;
  reason: string | null;
  created_at: string;
  profiles: {
    id: string;
    username: string;
    display_name: string | null;
    profile_image_url: string | null;
  } | null;
}

export const BlockedUsersList: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch blocked users
  const { data: blockedUsers = [], isLoading } = useQuery({
    queryKey: ['blocked-users', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      // First get the blocks
      const { data: blocks, error: blocksError } = await supabase
        .from('user_blocks')
        .select('id, blocked_id, reason, created_at')
        .eq('blocker_id', user.id)
        .order('created_at', { ascending: false });

      if (blocksError) {
        console.error('Error fetching blocked users:', blocksError);
        throw blocksError;
      }

      if (!blocks || blocks.length === 0) return [];

      // Then get the profile data for each blocked user
      const blockedUserIds = blocks.map(block => block.blocked_id);
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, username, display_name, profile_image_url')
        .in('id', blockedUserIds);

      if (profilesError) {
        console.error('Error fetching blocked user profiles:', profilesError);
        throw profilesError;
      }

      // Combine blocks with profile data
      const result = blocks.map(block => ({
        ...block,
        profiles: profiles?.find(profile => profile.id === block.blocked_id) || null
      }));

      return result;
    },
    enabled: !!user?.id,
  });

  // Unblock user mutation
  const unblockMutation = useMutation({
    mutationFn: async (blockId: string) => {
      const { error } = await supabase
        .from('user_blocks')
        .delete()
        .eq('id', blockId)
        .eq('blocker_id', user?.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blocked-users', user?.id] });
      toast.success('User unblocked successfully');
    },
    onError: (error) => {
      console.error('Error unblocking user:', error);
      toast.error('Failed to unblock user. Please try again.');
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-4">
        <div className="text-sm text-muted-foreground">Loading blocked users...</div>
      </div>
    );
  }

  if (blockedUsers.length === 0) {
    return (
      <div className="text-center py-6">
        <UserX className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
        <p className="text-sm text-muted-foreground">No blocked users</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {blockedUsers.map((block) => {
        const profile = block.profiles;
        if (!profile) return null;

        return (
          <div
            key={block.id}
            className="flex items-center justify-between p-3 bg-muted/10 rounded-lg border border-border/50"
          >
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={profile.profile_image_url || ''} />
                <AvatarFallback className="bg-primary/10 text-primary">
                  {(profile.display_name?.charAt(0) || profile.username?.charAt(0) || 'U').toUpperCase()}
                </AvatarFallback>
              </Avatar>
              
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">
                  {profile.display_name || profile.username}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  @{profile.username}
                </p>
                {block.reason && (
                  <p className="text-xs text-muted-foreground mt-1 truncate">
                    Reason: {block.reason}
                  </p>
                )}
              </div>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => unblockMutation.mutate(block.id)}
              disabled={unblockMutation.isPending}
              className="shrink-0"
            >
              <Trash2 className="h-3 w-3 mr-1" />
              {unblockMutation.isPending ? 'Unblocking...' : 'Unblock'}
            </Button>
          </div>
        );
      })}
    </div>
  );
};